import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import CandidateDashboard from "@/pages/CandidateDashboard";
import HotelDashboard from "@/pages/HotelDashboard";
import HotelOnboarding from "@/pages/HotelOnboarding";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentResult from "@/pages/PaymentResult";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import HotelTermsPage from "@/pages/HotelTermsPage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import ForHotelsPage from "@/pages/ForHotelsPage";
import JobsListPage, { JobDetailPage } from "@/pages/JobsPage";
import { CookieBanner } from "@/components/shared/CookieBanner";
import { authAPI } from "@/lib/api";
import { LanguageProvider } from "@/lib/i18n";
import { HelmetProvider } from "react-helmet-async";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    const fullUrl = window.location.hash + window.location.search;
    if (fullUrl.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Also skip if on /auth/callback path (OAuth return)
    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem("session_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) {
    const redirect = user.role === "admin" ? "/admin" : user.role === "hotel" ? "/hotel" : "/candidate";
    return <Navigate to={redirect} replace />;
  }
  return children;
}

function SmartHome() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "hotel") return <Navigate to="/hotel" replace />;
  return <Navigate to="/candidate" replace />;
}

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle OAuth callback: session_id in hash or query params on any path
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/candidate/*" element={
        <ProtectedRoute roles={["candidate"]}><CandidateDashboard /></ProtectedRoute>
      } />
      <Route path="/hotel/*" element={
        <ProtectedRoute roles={["hotel"]}><HotelDashboard /></ProtectedRoute>
      } />
      <Route path="/hotel/onboarding" element={
        <ProtectedRoute roles={["hotel"]}><HotelOnboarding /></ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/payment/success" element={<PaymentResult type="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult type="cancel" />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/hotel-terms" element={<HotelTermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/for-hotels" element={<ForHotelsPage />} />
      <Route path="/jobs" element={<JobsListPage />} />
      <Route path="/jobs/:slug" element={<JobDetailPage />} />
      <Route path="/jobs/:country/:region" element={<JobDetailPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
